/* Feature carousel enhancements. The track scrolls natively (touch, trackpad, keyboard) without this
   file; it adds the arrow buttons (one card per click, disabled at either end) and mouse drag-to-scroll. */
if (!customElements.get('feature-carousel')) {
  customElements.define('feature-carousel', class FeatureCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      if (!this.track || !this.prev || !this.next) return;

      this.prev.addEventListener('click', () => this.go(-1));
      this.next.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
      if (window.ResizeObserver) new ResizeObserver(() => this.queueUpdate()).observe(this.track);
      this.enableDrag();
      this.update();
    }

    step() {
      const card = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
    }

    smooth() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    go(direction) {
      this.track.scrollBy({ left: direction * this.step(), behavior: this.smooth() });
    }

    // Mouse only: touch and trackpads already scroll natively.
    enableDrag() {
      const track = this.track;
      let startX = 0;
      let startLeft = 0;
      let dragging = false;
      let moved = false;

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (track.scrollWidth <= track.clientWidth) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startLeft = track.scrollLeft;
      });

      track.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const dx = event.clientX - startX;
        if (!moved && Math.abs(dx) < 5) return; // let plain clicks through
        if (!moved) {
          moved = true;
          track.setPointerCapture(event.pointerId);
          track.classList.add('is-dragging'); // turns off snapping while dragging
        }
        track.scrollLeft = startLeft - dx;
      });

      const end = (event) => {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        // A deliberate drag (60px+) moves at least one card that way; a short one settles back.
        const step = this.step();
        const dx = startX - event.clientX;
        const from = Math.round(startLeft / step);
        const cards = Math.abs(dx) < 60 ? 0 : Math.max(1, Math.round(Math.abs(dx) / step));
        const target = (from + Math.sign(dx) * cards) * step;
        track.classList.remove('is-dragging');
        track.scrollTo({ left: target, behavior: this.smooth() });
      };
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);

      // A drag should not count as a click on whatever is under the pointer.
      track.addEventListener('click', (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }, true);
      track.addEventListener('dragstart', (event) => event.preventDefault());
    }

    queueUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const scrollable = max > 1;
      this.prev.hidden = !scrollable;
      this.next.hidden = !scrollable;
      this.track.classList.toggle('is-draggable', scrollable);
      this.prev.setAttribute('aria-disabled', String(this.track.scrollLeft <= 1));
      this.next.setAttribute('aria-disabled', String(this.track.scrollLeft >= max - 1));
    }
  });
}
